import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { UnifiedAuthProvider, useUnifiedAuth } from './contexts/UnifiedAuthContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ChatwootProvider, useChatwoot } from './contexts/ChatwootContext';
import { GroupsProvider } from './contexts/GroupsContext';
import { PageActionsProvider } from './contexts/PageActionsContext';
import { LoadingScreen } from './components/LoadingScreen';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Navigation } from './components/Navigation';
import { IframeDevToggle } from './components/IframeDevToggle';
import { ContactsPage } from './pages/ContactsPage';
import { WhatsAppConnectionsPage } from './pages/WhatsAppConnectionsPage';
import { GruposWhatsApp } from './pages/GruposWhatsApp';
import { CampaignsPage } from './pages/CampaignsPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { SettingsPage } from './pages/SettingsPage';
import { UsersPage } from './pages/UsersPage';
import { SuperAdminPage } from './pages/SuperAdminPage';
import { SuperAdminDashboard } from './pages/SuperAdminDashboard';
import { SuperAdminManagerPage } from './pages/SuperAdminManagerPage';
import { SuperAdminDashboardPage } from './pages/superadmin/SuperAdminDashboardPage';
import { SuperAdminTenantsPage } from './pages/superadmin/SuperAdminTenantsPage';
import { useGlobalSettings } from './hooks/useGlobalSettings';
import { TooltipProvider } from './contexts/TooltipContext';
import './styles/globals.css';

function AppContent() {
  const { isAuthenticated, isSuperAdmin } = useUnifiedAuth();
  const { isInChatwoot, isAuthReady, isLoading: chatwootLoading } = useChatwoot();
  const { settings } = useGlobalSettings();
  const { canUseWhatsWeb, canUseOfficialApi } = useTenant();

  // Aplicar meta tags dinâmicas (título e favicon)
  useEffect(() => {
    if (settings?.pageTitle) {
      document.title = settings.pageTitle;
    }
    if (settings?.faviconUrl) {
      const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
      existingFavicons.forEach(favicon => favicon.remove());
      const favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.type = 'image/png';
      favicon.href = settings.faviconUrl + '?v=' + Date.now();
      document.head.appendChild(favicon);
      const shortcutIcon = document.createElement('link');
      shortcutIcon.rel = 'shortcut icon';
      shortcutIcon.type = 'image/png';
      shortcutIcon.href = settings.faviconUrl + '?v=' + Date.now();
      document.head.appendChild(shortcutIcon);
    }
  }, [settings?.pageTitle, settings?.faviconUrl]);

  // Remove any banners dynamically
  useEffect(() => {
    const removeBanners = () => {
      const elements = Array.from(document.querySelectorAll('*')).filter(el =>
        el.textContent && el.textContent.includes('BUILD 2025-09-17-19:02')
      );
      elements.forEach(el => { el.remove(); });
    };
    removeBanners();
    const interval = setInterval(removeBanners, 5000);
    return () => { clearInterval(interval); };
  }, []);

  // ========================================================================
  // MODO STANDALONE: Se não está no iframe, mostrar mensagem
  // ========================================================================
  if (!isInChatwoot) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="text-center max-w-lg p-8">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--text-muted)' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            Acesse pela Plataforma
          </h1>
          <p className="text-lg mb-2" style={{ color: 'var(--text-secondary)' }}>
            O Broadcast deve ser acessado através da plataforma Konverte.
          </p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Entre na sua conta Konverte e acesse o Broadcast pelo menu lateral.
          </p>
        </div>
      </div>
    );
  }

  // ========================================================================
  // LOADING: Aguardando PostMessage ou auto-provision
  // ========================================================================
  if (chatwootLoading || !isAuthReady) {
    return (
      <LoadingScreen 
        message="Conectando à plataforma..."
        submessage="Aguarde enquanto configuramos sua sessão"
      />
    );
  }

  // ========================================================================
  // AUTH FALHOU: PostMessage recebido mas auto-provision falhou
  // ========================================================================
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="text-center max-w-lg p-8">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            Erro de Autenticação
          </h1>
          <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>
            Não foi possível configurar sua sessão. Tente recarregar a página.
          </p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Se o problema persistir, entre em contato com o suporte.
          </p>
        </div>
      </div>
    );
  }

  // ========================================================================
  // APP AUTENTICADO
  // ========================================================================
  return (
    <div className="min-h-screen transition-colors flex flex-col" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <Navigation />

      <main className="main-content-horizontal flex-1 flex flex-col">
        <Routes>
          <Route path="/" element={<Navigate to="/contatos" replace />} />
          <Route
            path="/contatos"
            element={
              <ProtectedRoute>
                <ContactsPage />
              </ProtectedRoute>
            }
          />
          {/* Rotas WhatsApp Web - Apenas se allowWhatsWeb = true */}
          {canUseWhatsWeb() && (
            <>
              <Route
                path="/whatsapp"
                element={
                  <ProtectedRoute>
                    <WhatsAppConnectionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/grupos"
                element={
                  <ProtectedRoute>
                    <GruposWhatsApp />
                  </ProtectedRoute>
                }
              />
            </>
          )}
          <Route
            path="/campanhas"
            element={
              <ProtectedRoute>
                <CampaignsPage />
              </ProtectedRoute>
            }
          />
          {/* Rotas API Oficial - Apenas se allowOfficialApi = true */}
          {canUseOfficialApi() && (
            <Route
              path="/templates"
              element={
                <ProtectedRoute>
                  <TemplatesPage />
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/configuracoes"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          {/* Super Admin Routes — Visíveis apenas se isSuperAdmin */}
          {isSuperAdmin && (
            <>
              <Route
                path="/superadmin"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin/analytics-dashboard"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin/empresas"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminManagerPage initialTab="tenants" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin/plataformas"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminManagerPage initialTab="platforms" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin/tooltips"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminManagerPage initialTab="tooltips" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin/usuarios"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminManagerPage initialTab="users" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin/integracoes"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminManagerPage initialTab="integrations" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin/ia"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminManagerPage initialTab="ai" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin/analytics-tenants"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminTenantsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin/configuracoes"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminManagerPage initialTab="general" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin/aparencia"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminManagerPage initialTab="appearance" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin/backup"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminManagerPage initialTab="backup" />
                  </ProtectedRoute>
                }
              />
              {/* Rotas antigas (redirecionar) */}
              <Route path="/super-admin" element={<Navigate to="/superadmin" replace />} />
              <Route
                path="/superadmin/dashboard"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin/tenants"
                element={
                  <ProtectedRoute superAdminOnly={true}>
                    <SuperAdminPage />
                  </ProtectedRoute>
                }
              />
            </>
          )}
          <Route
            path="/usuarios"
            element={
              <ProtectedRoute adminOnly={true}>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          {/* Fallback: qualquer rota não encontrada redireciona para contatos */}
          <Route path="*" element={<Navigate to="/contatos" replace />} />
        </Routes>
      </main>
      
      {/* 🎯 DEV: Toggle Iframe Mode */}
      <IframeDevToggle />
    </div>
  );
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <ChatwootProvider>
          <UnifiedAuthProvider>
            <TenantProvider>
              <GroupsProvider>
                <TooltipProvider>
                  <PageActionsProvider>
                    <AppContent />
                    <Toaster
                      position="top-center"
                      toastOptions={{
                        duration: 4000,
                        style: {
                          background: '#1e293b',
                          color: '#fff',
                          borderRadius: '12px',
                          fontSize: '14px',
                          padding: '12px 16px',
                        },
                        success: {
                          style: {
                            background: '#3b3f44',
                          },
                        },
                        error: {
                          style: {
                            background: '#ef4444',
                          },
                        },
                      }}
                    />
                  </PageActionsProvider>
                </TooltipProvider>
              </GroupsProvider>
            </TenantProvider>
          </UnifiedAuthProvider>
        </ChatwootProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;